import { NavBar } from "../navbar";
import { Footer } from "../footer";
import {
  useCheckDeviceStatus,
  useSiteDownloadStatus,
  useDownloadSensorData,
} from "../../api/queries/sitedownloadstatus";
import { Loading } from "../loadingContext";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { useState, useEffect } from "react";
import { useAlerts } from "../alerts";
import { Tooltip } from "react-tooltip";

export const Status = () => {
  const { addAlert } = useAlerts();
  const siteDownloadStatus = useSiteDownloadStatus();
  const checkDeviceStatus = useCheckDeviceStatus();
  const downloadSensorData = useDownloadSensorData();

  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [processedStatus, setProcessedStatus] = useState([]);

  useEffect(() => {
    if (siteDownloadStatus.data) {
      const groupedStatus = siteDownloadStatus.data.reduce((acc, status) => {
        const deviceID = status.data().DeviceID;
        if (!acc[deviceID]) {
          acc[deviceID] = [];
        }
        acc[deviceID].push({ ...status.data(), id: status.id });
        return acc;
      }, {});

      const processed = Object.values(groupedStatus).map((group) => {
        // Sort by StartDate descending to get the latest first
        group.sort((a, b) => {
          const aStartDate = a.StartDate ? a.StartDate.toMillis() : 0;
          const bStartDate = b.StartDate ? b.StartDate.toMillis() : 0;
          return bStartDate - aStartDate;
        });
        return group[0]; // Return only the latest (first after sorting)
      });
      
      setProcessedStatus(processed);
    }
  }, [siteDownloadStatus.data]);

  const onDownloadSensorData = async (devices) => {
    setIsDisabled(true);
    await downloadSensorData.mutateAsync(devices);
    setIsDisabled(false);
  };

  const onCheckDevice = async (device) => {
    let deviceID = device.DeviceID;
    try {
      setLoading(true);
      let response = await checkDeviceStatus.mutateAsync(device);
      setLoading(false);
      if (response["data"] === "error") {
        let alert = `There was an error retrieving data from device ${deviceID}`;
        addAlert({ message: alert, type: "error" });
      } else {
        console.log("Device response:", response);
        let data = response["data"];
        let alert = (
          <>
            {`Device ${deviceID}`}
            <br />
            {`${data["Datetime"]}`}
            <br />
            {`Nitrate ${data["Nitrate Nitrogen(pnt)"]}`}
          </>
        );
        addAlert({ message: alert, type: "success" });
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  return (
    <>
      {(siteDownloadStatus.isLoading || loading) && <Loading />}
      <div>
        <NavBar />
        <div className="min-h-screen">
          <h1 className="text-3xl p-2">
            Download Status
            <button
              disabled={isDisabled}
              className="m-2 btn btn-primary"
              onClick={(e) => {
                onDownloadSensorData(processedStatus);
              }}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
          </h1>
          <div className="overflow-ellipsis p-10">
            <table className="table table-fixed table-zebra">
              <thead>
                <tr>
                  <th className="w-20">Device</th>
                  <th className="w-60">Site</th>
                  <th className="w-20">Status</th>
                  <th>Last Download Date</th>
                  <th className="w-80">Message</th>
                  <th>Type</th>
                  <th>IP Address</th>
                  <th>Port</th>
                  <th>Frequency</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {siteDownloadStatus.isLoading ? (
                  <tr><td colSpan="10"><Loading /></td></tr>
                ) : siteDownloadStatus.isError ? (
                  <tr><td colSpan="10">Error loading data</td></tr>
                ) : (
                  processedStatus?.map((s) => (
                    <tr key={s.id}>
                      <td>{s.DeviceID}</td>
                      <td>{s.SiteName}</td>
                      <td>{s.SiteStatusDescription}</td>
                      <td>
                        {s.LatestDate?.toDate()
                          .toISOString()
                          .substring(0, 10)}
                      </td>
                      <td>
                        <div className="line-clamp-3 overflow-clip">
                          {s.Message}
                        </div>
                      </td>
                      <td>{s.SensorDataTypeDescription}</td>
                      <td>{s.IPAddress}</td>
                      <td>{s.Port}</td>
                      <td>{s.DownloadFrequencyDescription}</td>
                      <td>
                        {s.SiteTypeDescription == "Trusense" && (
                          <button
                            className="btn btn-ghost btn-xs"
                            data-tooltip-id="button-tooltip"
                            data-tooltip-content="Check the latest status and nitrate reading of this sensor"
                            onClick={() => onCheckDevice(s)}
                          >
                            Check
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
      <Tooltip id="button-tooltip" place="top" />
    </>
  );
};
